import React, { useState } from 'react';
import { ReviewDisplayProps, Review, ReviewResponseData } from '../../types/review-types';
import ReviewResponseForm from './ReviewResponseForm';

const ITEMS_PER_PAGE = 10;

export const ReviewDisplayComponent: React.FC<ReviewDisplayProps> = ({
  reviews,
  showPagination = true,
  showResponses = true,
  allowResponse = false,
  currentUserId,
  loading = false,
  error,
  onLoadMore,
  onSubmitResponse
}) => {
  const [expandedReviews, setExpandedReviews] = useState<Set<string>>(new Set());
  const [respondingToReview, setRespondingToReview] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(reviews.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentReviews = showPagination ? reviews.slice(startIndex, endIndex) : reviews;

  const toggleExpanded = (reviewId: string) => {
    const newExpanded = new Set(expandedReviews);
    if (newExpanded.has(reviewId)) {
      newExpanded.delete(reviewId);
    } else {
      newExpanded.add(reviewId);
    }
    setExpandedReviews(newExpanded);
  };

  const handleResponseSubmit = async (reviewId: string, responseData: ReviewResponseData) => {
    if (onSubmitResponse) {
      await onSubmitResponse(reviewId, responseData);
      setRespondingToReview(null);
    }
  };

  const handleResponseCancel = () => {
    setRespondingToReview(null);
  };

  const canRespondToReview = (review: Review): boolean => {
    if (!allowResponse || !currentUserId || !onSubmitResponse) return false;
    
    // User can respond if they are the reviewee and there's no existing response
    return review.revieweeId === currentUserId && !review.response;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderStars = (rating: number, size: 'sm' | 'md' = 'sm') => {
    const sizeClass = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
    
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            className={`${sizeClass} ${
              star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
            }`}
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
        <span className="ml-2 text-sm text-gray-600">{rating.toFixed(1)}</span>
      </div>
    );
  };

  const renderCategoryRatings = (categoryRatings: Record<string, number>) => {
    const categories = Object.entries(categoryRatings);
    if (categories.length === 0) return null;

    const categoryLabels: Record<string, string> = {
      cleanliness: 'Cleanliness',
      accuracy: 'Accuracy',
      communication: 'Communication',
      location: 'Location',
      value: 'Value',
      guestCommunication: 'Communication',
      guestCleanliness: 'Cleanliness',
      houseRulesAdherence: 'House Rules'
    };

    return (
      <div className="mt-3 space-y-2">
        <h4 className="text-sm font-medium text-gray-700">Category Ratings</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {categories.map(([category, rating]) => (
            <div key={category} className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                {categoryLabels[category] || category}
              </span>
              {renderStars(rating)}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderReviewCard = (review: Review) => {
    const isExpanded = expandedReviews.has(review.reviewId);
    const showExpandButton = review.reviewText.length > 200;
    const displayText = isExpanded || !showExpandButton 
      ? review.reviewText 
      : review.reviewText.substring(0, 200) + '...';

    return (
      <div key={review.reviewId} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        {/* Reviewer Info */}
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            {review.reviewerPhotoUrl ? (
              <img
                src={review.reviewerPhotoUrl}
                alt={review.reviewerName}
                className="w-12 h-12 rounded-full object-cover"
              />
            ) : (
              <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center">
                <span className="text-gray-600 font-medium text-lg">
                  {review.reviewerName.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  {review.reviewerName}
                </h3>
                <p className="text-sm text-gray-500">
                  {formatDate(review.createdAt)}
                </p>
              </div>
              {renderStars(review.overallRating, 'md')}
            </div>

            {/* Category Ratings */}
            {Object.keys(review.categoryRatings).length > 0 && (
              renderCategoryRatings(review.categoryRatings)
            )}

            {/* Review Text */}
            <div className="mt-4">
              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                {displayText}
              </p>
              {showExpandButton && (
                <button
                  onClick={() => toggleExpanded(review.reviewId)}
                  className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium focus:outline-none"
                >
                  {isExpanded ? 'Show less' : 'Show more'}
                </button>
              )}
            </div>

            {/* Response Section */}
            {showResponses && review.response && (
              <div className="mt-6 pl-4 border-l-4 border-gray-200 bg-gray-50 rounded-r-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-sm font-medium text-gray-700">Response</span>
                  <span className="text-xs text-gray-500">
                    {formatDate(review.response.createdAt)}
                  </span>
                </div>
                <p className="text-gray-700 text-sm leading-relaxed">
                  {review.response.responseText}
                </p>
              </div>
            )}

            {/* Response Form */}
            {respondingToReview === review.reviewId && (
              <div className="mt-6">
                <ReviewResponseForm
                  reviewId={review.reviewId}
                  deadline={new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString()} // 14 days from now
                  onSubmit={(responseData) => handleResponseSubmit(review.reviewId, responseData)}
                  onCancel={handleResponseCancel}
                />
              </div>
            )}

            {/* Response Button */}
            {canRespondToReview(review) && respondingToReview !== review.reviewId && (
              <div className="mt-4">
                <button
                  onClick={() => setRespondingToReview(review.reviewId)}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium focus:outline-none"
                >
                  Respond to this review
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, index) => (
          <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm animate-pulse">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-gray-300 rounded-full"></div>
              <div className="flex-1 space-y-3">
                <div className="h-4 bg-gray-300 rounded w-1/4"></div>
                <div className="h-3 bg-gray-300 rounded w-1/6"></div>
                <div className="space-y-2">
                  <div className="h-3 bg-gray-300 rounded"></div>
                  <div className="h-3 bg-gray-300 rounded w-5/6"></div>
                  <div className="h-3 bg-gray-300 rounded w-4/6"></div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center space-x-2">
          <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <span className="text-red-800 font-medium">Error loading reviews</span>
        </div>
        <p className="text-red-700 mt-2">{error}</p>
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className="text-center py-12">
        <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No reviews yet</h3>
        <p className="text-gray-500">Be the first to share your experience!</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Reviews List */}
      <div className="space-y-4">
        {currentReviews.map(renderReviewCard)}
      </div>

      {/* Pagination */}
      {showPagination && totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-gray-200 pt-6">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700">
              Showing {startIndex + 1} to {Math.min(endIndex, reviews.length)} of {reviews.length} reviews
            </span>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            <div className="flex space-x-1">
              {[...Array(totalPages)].map((_, index) => {
                const page = index + 1;
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-2 text-sm font-medium rounded-md ${
                      currentPage === page
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Load More Button (alternative to pagination) */}
      {!showPagination && onLoadMore && (
        <div className="text-center pt-6">
          <button
            onClick={onLoadMore}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Load More Reviews
          </button>
        </div>
      )}
    </div>
  );
};

export default ReviewDisplayComponent;